import { useState, useEffect, useRef, useCallback } from 'react'
import { Volume2, Play, Pause, Loader2 } from 'lucide-react'
import { cn } from '../../lib/utils'
import { useAudioPlayer } from '../../hooks/useAudioPlayer'
import { formatDuration } from '../../lib/format-duration'
import { t } from '@craft-agent/shared/i18n'

export interface VoicePlayerWidgetProps {
  /** 音频文件的本地存储路径 */
  storedPath: string
  /** 转录文本（可选） */
  transcription?: string
  /** 加载音频文件为 data URL 的函数 */
  loadAudioDataUrl: (path: string) => Promise<string>
}

/**
 * 语音消息播放组件
 * 提供播放/暂停控制、进度条、时长显示和转录文本展示
 */
export function VoicePlayerWidget({
  storedPath,
  transcription,
  loadAudioDataUrl,
}: VoicePlayerWidgetProps) {
  const [audioDataUrl, setAudioDataUrl] = useState<string | null>(null)
  const [loadError, setLoadError] = useState(false)
  const progressBarRef = useRef<HTMLDivElement>(null)
  const isDraggingRef = useRef(false)

  // 加载音频文件
  useEffect(() => {
    let cancelled = false
    setLoadError(false)
    setAudioDataUrl(null)

    loadAudioDataUrl(storedPath)
      .then((url) => {
        console.log('[VoicePlayerWidget] Audio loaded, url length:', url?.length, 'prefix:', url?.substring(0, 60));
        if (!cancelled) setAudioDataUrl(url)
      })
      .catch((err) => {
        console.error('[VoicePlayerWidget] Audio load failed:', err);
        if (!cancelled) setLoadError(true)
      })

    return () => {
      cancelled = true
    }
  }, [storedPath, loadAudioDataUrl])

  const { state, currentTime, duration, errorType, play, pause, seek } = useAudioPlayer({
    audioDataUrl: loadError ? null : audioDataUrl,
  })

  // 计算进度百分比
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0

  // 根据点击/拖动位置计算跳转时间
  const seekFromPosition = useCallback(
    (clientX: number) => {
      const bar = progressBarRef.current
      if (!bar || duration <= 0) return
      const rect = bar.getBoundingClientRect()
      const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
      seek(ratio * duration)
    },
    [duration, seek],
  )

  // 进度条点击跳转
  const handleProgressClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (state === 'loading' || state === 'error') return
      seekFromPosition(e.clientX)
    },
    [state, seekFromPosition],
  )

  // 进度条拖动
  const handleProgressMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (state === 'loading' || state === 'error') return
      e.preventDefault()
      isDraggingRef.current = true

      const handleMouseMove = (ev: MouseEvent) => {
        if (isDraggingRef.current) seekFromPosition(ev.clientX)
      }
      const handleMouseUp = () => {
        isDraggingRef.current = false
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [state, seekFromPosition],
  )

  // 判断是否处于加载中状态（文件加载中 或 音频解码中）
  const isLoading = !loadError && (audioDataUrl === null || state === 'loading')
  // 判断是否有错误
  const hasError = loadError || state === 'error'
  // 播放按钮是否禁用
  const isPlayDisabled = isLoading || hasError

  // 错误提示文本
  const getErrorText = () => {
    if (loadError || errorType === 'unavailable') return t('settings.imBinding.voicePlayer.audioUnavailable')
    if (errorType === 'unsupported') return t('settings.imBinding.voicePlayer.audioUnsupported')
    return null
  }

  const errorText = getErrorText()

  return (
    <div className="flex flex-col gap-1.5 rounded-[8px] bg-background px-3 py-2 min-w-[220px] max-w-[320px]">
      {/* 播放控件行 */}
      <div className="flex items-center gap-2">
        {/* 语音图标 */}
        <Volume2 className="h-4 w-4 shrink-0 text-muted-foreground" />

        {/* 播放/暂停按钮 */}
        <button
          type="button"
          onClick={state === 'playing' ? pause : play}
          disabled={isPlayDisabled}
          className={cn(
            'flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-colors',
            isPlayDisabled
              ? 'cursor-not-allowed text-muted-foreground/50'
              : 'cursor-pointer text-foreground hover:text-foreground/80',
          )}
          aria-label={state === 'playing' ? t('settings.imBinding.voicePlayer.pause') : t('settings.imBinding.voicePlayer.play')}
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : state === 'playing' ? (
            <Pause className="h-4 w-4" />
          ) : (
            <Play className="h-4 w-4" />
          )}
        </button>

        {/* 进度条 */}
        <div
          ref={progressBarRef}
          role="progressbar"
          aria-valuenow={Math.round(progress)}
          aria-valuemin={0}
          aria-valuemax={100}
          className={cn(
            'relative flex-1 h-1 rounded-full bg-muted cursor-pointer',
            isPlayDisabled && 'cursor-default opacity-50',
          )}
          onClick={handleProgressClick}
          onMouseDown={handleProgressMouseDown}
        >
          <div
            className="absolute left-0 top-0 h-full rounded-full bg-foreground/60 transition-[width] duration-100"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* 时长显示 */}
        <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
          {formatDuration(duration)}
        </span>
      </div>

      {/* 错误提示 */}
      {errorText && (
        <p className="text-xs text-destructive">{errorText}</p>
      )}

      {/* 转录文本 */}
      {transcription && (
        <p className="text-xs text-muted-foreground leading-relaxed break-words whitespace-pre-wrap">
          {transcription}
        </p>
      )}
    </div>
  )
}
